/**
 * Application state and the actions that change it.
 *
 * One store rather than per-component fetching, because almost every action mutates the
 * same JSONB document and the whole workspace has to reflect it: regenerating a scene's
 * visual changes the scene card, the scene strip, the export panel's readiness and the
 * activity log. Reloading the project after each mutation and rendering from that keeps
 * the screen honest about what the server actually holds, rather than optimistically
 * showing what we hoped it would do.
 *
 * `busy` and `failed` are keyed per element ("visual:scn_1", "concept:hook") because the
 * design shows progress and failure per element, not one global spinner.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import * as api from './api';
import {ApiError} from './api';
import type {
  Aspect,
  ArchiveAsset,
  Concept,
  ConceptField,
  Job,
  Project,
  ProjectSummary,
  Scene,
  SceneTextField,
  Screen,
  SceneView,
  User,
  VoiceItem,
} from './types';

const USER_KEY = 'studio_user';

export interface RenderState {
  phase: 'idle' | 'running' | 'done' | 'failed';
  url: string;
  message: string;
  /** From a HEAD on the file, not from the storyboard. */
  sizeBytes: number | null;
  /** From the video element's metadata — the exported length, not the planned one. */
  durationS: number;
}

export interface ToastState {
  /** The running job's kind; the view maps it to its Arabic label. */
  kind: string;
  detail: string;
  percent: number;
  step: number;
}

export type Modal =
  | {kind: 'signin'; error?: string}
  | {kind: 'archive'; assets?: ArchiveAsset[]; error?: string}
  | {kind: 'voices'; sceneId?: string; voices?: VoiceItem[]; error?: string};

interface State {
  user: User | null;
  screen: Screen;
  /** Brief-screen form state, before a project exists. */
  brief: string;
  aspect: Aspect;
  duration: number;
  projects: ProjectSummary[];
  project: Project | null;
  busy: Record<string, boolean>;
  failed: Record<string, string>;
  cuesOpen: Record<string, boolean>;
  focusedScene: string | null;
  render: RenderState;
  toast: ToastState | null;
  rateLimited: boolean;
  modal: Modal | null;
}

const IDLE_RENDER: RenderState = {
  phase: 'idle',
  url: '',
  message: '',
  sizeBytes: null,
  durationS: 0,
};

const INITIAL: State = {
  user: null,
  screen: 'brief',
  brief: '',
  aspect: '9:16',
  duration: 20,
  projects: [],
  project: null,
  busy: {},
  failed: {},
  cuesOpen: {},
  focusedScene: null,
  render: IDLE_RENDER,
  toast: null,
  rateLimited: false,
  modal: null,
};

export interface Store extends State {
  /* form state */
  setBrief(value: string): void;
  setAspect(value: Aspect): void;
  adjustDuration(delta: number): void;

  /* navigation */
  go(screen: Screen): void;
  newBrief(): void;
  focusScene(sceneId: string): void;
  toggleCues(sceneId: string): void;

  /* identity */
  signIn(displayName: string): Promise<void>;
  openSignIn(): void;

  /* lifecycle */
  create(): Promise<void>;
  open(projectId: string): Promise<void>;
  generate(): Promise<void>;

  /* concept */
  editConcept(field: ConceptField, value: string): Promise<void>;
  regenerateConcept(field?: ConceptField): Promise<void>;

  /* scenes */
  editScene(sceneId: string, field: SceneTextField, value: string): Promise<void>;
  regenerateScene(sceneId: string): Promise<void>;
  removeScene(sceneId: string): Promise<void>;

  /* visuals */
  generateVisual(sceneId: string): Promise<void>;
  archiveVisual(sceneId: string): Promise<void>;
  pickAlternative(sceneId: string, index: number): Promise<void>;
  uploadVisual(sceneId: string, file: File): Promise<void>;

  /* voice-over */
  regenerateVoiceover(sceneId: string, voice?: string, voiceId?: string): Promise<void>;

  /* batch pipeline */
  batchVisuals(): Promise<void>;
  batchArchive(): Promise<void>;
  batchVoiceovers(): Promise<void>;

  /* export */
  startRender(): Promise<void>;
  setRenderDuration(seconds: number): void;
  setProjectAspect(value: Aspect): void;

  /* overlays */
  openArchive(): Promise<void>;
  openVoiceLibrary(sceneId?: string): Promise<void>;
  selectVoiceForScene(sceneId: string, voiceId: string): Promise<void>;
  closeModal(): void;
  dismissToast(): void;

  /* derived */
  scenes(): Scene[];
  sceneView(scene: Scene): SceneView;
  isBusy(key: string): boolean;
  errorFor(key: string): string;
}

const StoreContext = createContext<Store | null>(null);

export function useStudio(): Store {
  const store = useContext(StoreContext);
  if (!store) throw new Error('useStudio must be used inside <StudioProvider>');
  return store;
}

/** A visual is playable only if we have a URL. Local media is root-relative. */
export function assetUrl(asset: {url: string; s3_key: string} | null): string {
  if (!asset) return '';
  if (asset.url) return asset.url;
  if (asset.s3_key.startsWith('media/')) return `/${asset.s3_key}`;
  // Archive assets stored in S3 under "archive/" are served via /media/archive/
  if (asset.s3_key.startsWith('archive/')) return `/media/${asset.s3_key}`;
  return '';
}

export const isVideoAsset = (asset: {mime: string} | null): boolean =>
  !!asset && asset.mime.startsWith('video/');

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

export function StudioProvider({children}: {children: ReactNode}) {
  const [state, setState] = useState<State>(INITIAL);

  // Async flows read state after awaits, where the closed-over copy is already stale.
  const ref = useRef(state);
  ref.current = state;

  const patch = useCallback((next: Partial<State>) => {
    setState((prev) => ({...prev, ...next}));
  }, []);

  const setBusy = useCallback((key: string, value: boolean) => {
    setState((prev) => {
      const busy = {...prev.busy};
      if (value) busy[key] = true;
      else delete busy[key];
      return {...prev, busy};
    });
  }, []);

  const setFailed = useCallback((key: string, message: string | null) => {
    setState((prev) => {
      const failed = {...prev.failed};
      if (message) failed[key] = message;
      else delete failed[key];
      return {...prev, failed};
    });
  }, []);

  /* --- identity ------------------------------------------------------------- */

  useEffect(() => {
    const stored = localStorage.getItem(USER_KEY);
    if (!stored) {
      patch({modal: {kind: 'signin'}});
      return;
    }
    try {
      const user = JSON.parse(stored) as User;
      patch({user});
    } catch {
      localStorage.removeItem(USER_KEY);
      patch({modal: {kind: 'signin'}});
    }
  }, [patch]);

  const refreshProjects = useCallback(async (userId: string) => {
    try {
      patch({projects: await api.listProjects(userId)});
    } catch {
      patch({projects: []});
    }
  }, [patch]);

  // Keyed on the id alone: the list is refreshed explicitly after a create, so re-running
  // this whenever `refreshProjects` is re-created would just duplicate that fetch.
  const userId = state.user?.id;
  useEffect(() => {
    if (userId) void refreshProjects(userId);
  }, [userId, refreshProjects]);

  /* --- project loading ------------------------------------------------------ */

  /**
   * Reload the project and re-derive export state from it, so reopening a project finds
   * the render it already has instead of an empty preview screen.
   */
  const reload = useCallback(async (projectId?: string) => {
    const {user, project, render} = ref.current;
    const id = projectId ?? project?.id;
    if (!user || !id) return;

    const fresh = await api.getProject(user.id, id);

    let nextRender = render;
    if (render.phase !== 'running') {
      const done = fresh.document.renders.filter((r) => r.status === 'done' && r.s3_key);
      const last = done[done.length - 1];
      if (last) {
        // s3_key may be a presigned URL (starts with http) or a local path (media/...)
        const url = last.s3_key.startsWith('http') ? last.s3_key : `/${last.s3_key}`;
        nextRender =
          url === render.url ? {...render, phase: 'done'} : {...IDLE_RENDER, phase: 'done', url};
      } else if (render.phase !== 'failed') {
        nextRender = IDLE_RENDER;
      }
    }

    patch({project: fresh, render: nextRender});

    if (nextRender.url && nextRender.sizeBytes === null) {
      const bytes = await api.probeSize(nextRender.url);
      if (bytes !== null) {
        setState((prev) => ({...prev, render: {...prev.render, sizeBytes: bytes}}));
      }
    }
  }, [patch]);

  /**
   * Run one element-scoped operation, reflecting its real state.
   *
   * Failures are captured against the element key rather than thrown on, because the
   * design shows failure in place — a failed scene visual must not take down the rest
   * of the workspace.
   */
  const run = useCallback(
    async (key: string, work: (userId: string, projectId: string) => Promise<unknown>) => {
      const {user, project} = ref.current;
      if (!user || !project) return;
      setBusy(key, true);
      setFailed(key, null);
      try {
        await work(user.id, project.id);
        await reload(project.id);
      } catch (e) {
        const error = e as ApiError;
        if (error.isRateLimit) patch({rateLimited: true});
        setFailed(key, error.message || 'فشلت العملية');
      } finally {
        setBusy(key, false);
      }
    },
    [patch, reload, setBusy, setFailed],
  );

  /* --- the queue ------------------------------------------------------------ */

  /**
   * Follow the queue until it drains, reporting progress from the jobs themselves.
   *
   * Each tick also nudges /internal/run-jobs so the app works whether or not the worker
   * container is up; when the worker holds the job that call finds nothing and we simply
   * wait. A failed job stops the loop and is surfaced — a stalled pipeline that keeps
   * spinning is the worst thing that can happen on stage.
   */
  const followQueue = useCallback(async () => {
    const {user, project} = ref.current;
    if (!user || !project) return;
    const projectId = project.id;

    for (let tick = 0; tick < 200; tick += 1) {
      // Reload project to check if pipeline is done
      try {
        await reload(projectId);
      } catch {
        // Keep following on transient read failures.
      }

      const fresh = ref.current.project;
      if (fresh && fresh.status === 'ready') break;

      let jobs: Job[];
      try {
        jobs = await api.listJobs(user.id, projectId);
      } catch {
        break;
      }

      const failure = jobs.find((j) => j.status === 'failed');
      if (failure) {
        setFailed('pipeline', `${failure.kind}: ${failure.error ?? 'فشل غير معروف'}`);
        break;
      }

      const pending = jobs.filter((j) => j.status === 'queued' || j.status === 'running');

      if (pending.length > 0) {
        const done = jobs.length - pending.length;
        patch({
          toast: {
            kind: pending[0]!.kind,
            detail: `${done} من ${jobs.length}`,
            percent: Math.round((done / jobs.length) * 100),
            step: done + 1,
          },
        });
      }

      // Nudge the queue in case the worker isn't running
      await api.runJobs(2);
      await sleep(1500);
    }

    patch({toast: null});
    try {
      await reload(projectId);
    } catch {
      // Nothing left to refresh.
    }
  }, [patch, reload, setFailed]);

  /* --- actions -------------------------------------------------------------- */

  const store = useMemo<Store>(() => {
    const withUser = <T,>(fn: (userId: string) => Promise<T>) => {
      const {user} = ref.current;
      return user ? fn(user.id) : Promise.resolve(undefined as T);
    };

    return {
      ...state,

      setBrief: (brief) => patch({brief}),
      setAspect: (aspect) => patch({aspect}),
      adjustDuration: (delta) =>
        setState((prev) => ({...prev, duration: Math.min(180, Math.max(5, prev.duration + delta))})),

      go: (screen) => patch({screen}),
      newBrief: () =>
        patch({
          project: null,
          brief: '',
          screen: 'brief',
          render: IDLE_RENDER,
          failed: {},
          focusedScene: null,
        }),
      focusScene: (sceneId) => {
        patch({focusedScene: sceneId});
        // After paint, so the card exists to scroll to.
        requestAnimationFrame(() => {
          const node = document.getElementById(`scene-${sceneId}`);
          if (node) {
            window.scrollTo({
              top: node.getBoundingClientRect().top + window.scrollY - 200,
              behavior: 'smooth',
            });
          }
        });
      },
      toggleCues: (sceneId) =>
        setState((prev) => ({
          ...prev,
          cuesOpen: {...prev.cuesOpen, [sceneId]: !prev.cuesOpen[sceneId]},
        })),

      openSignIn: () => patch({modal: {kind: 'signin'}}),
      signIn: async (displayName) => {
        const name = displayName.trim();
        if (!name) return;
        try {
          const user = await api.createUser(name);
          localStorage.setItem(USER_KEY, JSON.stringify(user));
          patch({user, modal: null});
        } catch (e) {
          patch({modal: {kind: 'signin', error: (e as ApiError).message}});
        }
      },

      create: async () => {
        const {user, brief, aspect, duration} = ref.current;
        if (!user) return;
        if (!brief.trim()) {
          setFailed('create', 'اكتب فكرة الفيديو أولًا');
          return;
        }
        if (brief.trim().length < 10) {
          setFailed('create', 'الفكرة قصيرة جدًا — اكتب جملة واحدة على الأقل تصف موضوع الفيديو');
          return;
        }
        setBusy('create', true);
        setFailed('create', null);
        try {
          const project = await api.createProject(user.id, {
            input_payload: brief.trim(),
            aspect_ratio: aspect,
            target_duration_s: duration,
          });
          patch({project, screen: 'workspace', render: IDLE_RENDER});
          await api.startGeneration(user.id, project.id);
          void refreshProjects(user.id);
          await followQueue();
        } catch (e) {
          const error = e as ApiError;
          if (error.isRateLimit) patch({rateLimited: true});
          setFailed('create', error.message);
        } finally {
          setBusy('create', false);
        }
      },

      open: async (projectId) =>
        withUser(async (userId) => {
          setBusy(`open:${projectId}`, true);
          setFailed('pipeline', null);
          try {
            const project = await api.getProject(userId, projectId);
            patch({project, screen: 'workspace', render: IDLE_RENDER, focusedScene: null});
            await reload(projectId);
          } catch (e) {
            setFailed('create', (e as ApiError).message);
          } finally {
            setBusy(`open:${projectId}`, false);
          }
        }),

      generate: async () => {
        await run('generate', (userId, projectId) => api.startGeneration(userId, projectId));
        await followQueue();
      },

      editConcept: (field, value) =>
        run(`concept:${field}`, (userId, projectId) =>
          api.patchConcept(userId, projectId, {[field]: value} as Partial<Concept>),
        ),

      regenerateConcept: (field) =>
        run(field ? `concept:${field}` : 'concept:all', (userId, projectId) =>
          api.regenerateConcept(userId, projectId, field),
        ),

      editScene: (sceneId, field, value) =>
        run(`edit:${sceneId}:${field}`, (userId, projectId) =>
          api.patchScene(userId, projectId, sceneId, {[field]: value}),
        ),

      regenerateScene: (sceneId) =>
        run(`scene:${sceneId}`, (userId, projectId) =>
          api.regenerateScene(userId, projectId, sceneId),
        ),

      removeScene: (sceneId) =>
        run(`scene:${sceneId}`, (userId, projectId) => api.deleteScene(userId, projectId, sceneId)),

      generateVisual: (sceneId) =>
        run(`visual:${sceneId}`, (userId, projectId) =>
          api.generateSceneVisual(userId, projectId, sceneId),
        ),

      archiveVisual: (sceneId) =>
        run(`visual:${sceneId}`, (userId, projectId) =>
          api.assignArchiveVisual(userId, projectId, sceneId),
        ),

      pickAlternative: (sceneId, index) =>
        run(`visual:${sceneId}`, (userId, projectId) =>
          api.selectAlternative(userId, projectId, sceneId, index),
        ),

      uploadVisual: (sceneId, file) =>
        run(`visual:${sceneId}`, (userId, projectId) =>
          api.uploadSceneVisual(userId, projectId, sceneId, file),
        ),

      regenerateVoiceover: (sceneId, voice, voiceId) =>
        run(`voiceover:${sceneId}`, (userId, projectId) =>
          api.generateSceneVoiceover(userId, projectId, sceneId, voice, voiceId),
        ),

      batchVisuals: async () => {
        await run('batch', (userId, projectId) => api.generateAllVisuals(userId, projectId));
        await followQueue();
      },

      batchArchive: () => run('batch', (userId, projectId) => api.matchAllFromArchive(userId, projectId)),

      batchVoiceovers: async () => {
        await run('batch', (userId, projectId) => api.generateAllVoiceovers(userId, projectId));
        await followQueue();
      },

      startRender: async () => {
        const {user, project} = ref.current;
        if (!user || !project) return;
        patch({render: {...IDLE_RENDER, phase: 'running'}});
        try {
          const result = await api.renderVideo(user.id, project.id);
          patch({render: {...IDLE_RENDER, phase: 'done', url: result.video_url}});
          const bytes = await api.probeSize(result.video_url);
          if (bytes !== null) {
            setState((prev) => ({...prev, render: {...prev.render, sizeBytes: bytes}}));
          }
        } catch (e) {
          const error = e as ApiError;
          if (error.isRateLimit) patch({rateLimited: true});
          patch({render: {...IDLE_RENDER, phase: 'failed', message: error.message}});
        }
        try {
          await reload(project.id);
        } catch {
          // Keep whatever outcome we already have.
        }
      },

      setRenderDuration: (seconds) =>
        setState((prev) =>
          Math.round(prev.render.durationS) === Math.round(seconds)
            ? prev
            : {...prev, render: {...prev.render, durationS: seconds}},
        ),

      setProjectAspect: (value) => {
        void run('aspect', (userId, projectId) =>
          api.patchProject(userId, projectId, {aspect_ratio: value}),
        );
      },

      openArchive: async () => {
        patch({modal: {kind: 'archive'}});
        try {
          const {assets} = await api.listArchive();
          patch({modal: {kind: 'archive', assets}});
        } catch (e) {
          patch({modal: {kind: 'archive', assets: [], error: (e as ApiError).message}});
        }
      },

      openVoiceLibrary: async (sceneId) => {
        patch({modal: {kind: 'voices', sceneId}});
        try {
          const voices = await api.listVoices(ref.current.user!.id);
          patch({modal: {kind: 'voices', sceneId, voices}});
        } catch (e) {
          patch({modal: {kind: 'voices', sceneId, voices: [], error: (e as ApiError).message}});
        }
      },

      selectVoiceForScene: async (sceneId, voiceId) => {
        patch({modal: null});
        await run(`voiceover:${sceneId}`, (userId, projectId) =>
          api.generateSceneVoiceover(userId, projectId, sceneId, undefined, voiceId),
        );
      },
      closeModal: () => patch({modal: null}),
      dismissToast: () => patch({toast: null}),

      scenes: () => [...(state.project?.document.scenes ?? [])].sort((a, b) => a.order - b.order),

      isBusy: (key) => !!state.busy[key],
      errorFor: (key) => state.failed[key] ?? '',

      sceneView: (scene) => {
        const visualBusy = !!state.busy[`visual:${scene.id}`];
        const voiceoverBusy = !!state.busy[`voiceover:${scene.id}`];
        const sceneBusy = !!state.busy[`scene:${scene.id}`];
        const visualError = state.failed[`visual:${scene.id}`] ?? '';
        const voiceoverError = state.failed[`voiceover:${scene.id}`] ?? '';
        const url = assetUrl(scene.visual);

        const visual = visualBusy
          ? 'busy'
          : visualError
            ? 'fail'
            : url
              ? 'ready'
              : 'empty';
        const voiceover = voiceoverBusy
          ? 'busy'
          : voiceoverError
            ? 'fail'
            : scene.voiceover?.s3_key
              ? 'ready'
              : 'empty';

        const status =
          visualBusy || voiceoverBusy || sceneBusy
            ? 'generating'
            : visual === 'fail' || voiceover === 'fail'
              ? 'failed'
              : visual === 'ready' && (voiceover === 'ready' || !scene.vo_text)
                ? 'ready'
                : 'draft';

        return {visual, voiceover, status, visualUrl: url, visualError, voiceoverError};
      },
    };
  }, [state, patch, run, reload, followQueue, refreshProjects, setBusy, setFailed]);

  return <StoreContext.Provider value={store}>{children}</StoreContext.Provider>;
}
